import * as actions from '@returnmates/client-core/src/constants/actionTypes'
import { DriverReliabilityStatus, Route } from '@returnmates/client-core/src/graphql/generated/api'
import { getRoute as getRouteSelector } from '@returnmates/client-core/src/selectors/admin'
import { SnackBarStatuses } from '@returnmates/client-core/src/type'
import { createAsyncAction } from '@returnmates/client-core/src/utils/reduxUtils'
import Button from '@returnmates/ui-core/src/components/Button'
import SwaySpinner from '@returnmates/ui-core/src/components/SwaySpinner'
import clsx from 'clsx'
import arrayMutators from 'final-form-arrays'
import { lazy, memo, Suspense, useCallback, useContext, useEffect, useState } from 'react'
import { Form } from 'react-final-form'
import { useDispatch, useSelector } from 'react-redux'

import { FlyoutSidebarContext } from '../../pages/Main'
import { isDummyDriver } from '../../pages/Routes/utils'
import UpdateDriverReliabilityModal from './components/UpdateDriverReliabilityModal'
import useStyles from './styles'

const EditRouteFormInner = lazy(() => import('./components/EditRouteFormInner'))

interface Props {
  id: string
}

function EditRouteForm({ id }: Props) {
  const classes = useStyles()
  const dispatch = useDispatch()
  const currentRoute: Route | null = useSelector(getRouteSelector)
  const { handleCloseSidebar } = useContext(FlyoutSidebarContext)
  const [isLoading, setIsLoading] = useState(false)
  const [isRouteUpdateLoading, setIsRouteUpdateLoading] = useState(false)
  const [isUpdateDriverReliabilityModal, setUpdateDriverReliabilityModal] = useState(false)
  const [oldDriverId, setOldDriverId] = useState<string | null>(null)

  const modifyRoute = useCallback(
    async (route: Route & { driverReliabilityStatus: DriverReliabilityStatus }) => {
      const isDriverTouched = currentRoute?.driver?.id !== route?.driver?.id
      const isDriverStatusTouched =
        currentRoute?.driverReliabilityStatus != route?.driverReliabilityStatus
      const oldDriverId = currentRoute?.driver?.id

      setIsRouteUpdateLoading(true)

      try {
        await createAsyncAction(
          dispatch,
          actions.modifyRoute.request({
            id,
            input: {
              offerAdjustment: route.offerAdjustment,
              driverId: route?.driver?.id || null,
            },
            isUpdateRoutePlan: isDriverTouched,
          }),
        )

        if (isDriverStatusTouched && route?.driverReliabilityStatus) {
          await createAsyncAction(
            dispatch,
            actions.createDriverReliability.request({
              input: {
                driverId: oldDriverId,
                routeId: currentRoute?.id,
                status: DriverReliabilityStatus[route.driverReliabilityStatus],
              },
            }),
          )
        }

        if (
          oldDriverId &&
          isDriverTouched &&
          !currentRoute.driverReliabilityStatus &&
          !isDummyDriver(currentRoute?.driver?.name || '')
        ) {
          setUpdateDriverReliabilityModal(true)
          setOldDriverId(oldDriverId)
        }
      } catch (error) {
        dispatch(
          actions.addSnackBar.request({
            type: SnackBarStatuses.ERROR,
            message: error,
          }),
        )
      } finally {
        setIsRouteUpdateLoading(false)
      }
    },
    [
      currentRoute?.driver?.id,
      currentRoute?.driver?.name,
      currentRoute?.driverReliabilityStatus,
      currentRoute?.id,
      dispatch,
      id,
    ],
  )

  const handleCloseUpdateDriverReliabilityModal = () => {
    setUpdateDriverReliabilityModal(false)
  }

  const getRoute = useCallback(async () => {
    if (id === currentRoute?.id) {
      return
    }

    setIsLoading(true)

    try {
      await createAsyncAction(
        dispatch,
        actions.getRoute.request({
          id,
        }),
      )
    } catch (error) {
      dispatch(
        actions.addSnackBar.request({
          type: SnackBarStatuses.ERROR,
          message: error,
        }),
      )
    } finally {
      setIsLoading(false)
    }
  }, [id, currentRoute?.id, dispatch])

  useEffect(() => {
    getRoute()
  }, [getRoute])

  useEffect(() => {
    return () => {
      dispatch(actions.clearRoute.request())
    }
  }, [dispatch])

  if (isLoading || isRouteUpdateLoading) {
    return <SwaySpinner classes={{ spinnerWrapper: classes.spinner }} />
  }

  return (
    <Form
      initialValues={{
        offerAdjustment: currentRoute?.offerAdjustment,
        driver: currentRoute?.driver,
        driverReliabilityStatus: currentRoute?.driverReliabilityStatus,
      }}
      onSubmit={modifyRoute}
      mutators={{ ...arrayMutators }}
      render={({ handleSubmit, pristine }) => (
        <>
          {currentRoute ? (
            <Suspense fallback={<SwaySpinner classes={{ spinnerWrapper: classes.spinner }} />}>
              <EditRouteFormInner />
            </Suspense>
          ) : null}
          <div className={classes.buttonsContainer}>
            <Button
              className={clsx(classes.button, classes.cancelButton)}
              onClick={handleCloseSidebar}
              label="Cancel"
              cancelButton
            />
            <Button
              className={clsx(classes.button, classes.saveButton)}
              onClick={pristine ? handleCloseSidebar : handleSubmit}
              label="Save"
              isLoading={isRouteUpdateLoading}
            />
          </div>

          <UpdateDriverReliabilityModal
            isOpen={isUpdateDriverReliabilityModal}
            onClose={handleCloseUpdateDriverReliabilityModal}
            driverId={oldDriverId}
          />
        </>
      )}
    />
  )
}

export default memo(EditRouteForm)
